/**
 * Opens the Help Scout Beacon on `data-helpscout-open` clicks. The Beacon SDK is
 * loaded inline in layout/theme.liquid.
 *
 * The button renders pre-loading (`is-loading`) in Liquid so the spinner shows
 * even before this deferred script runs; we clear it once the Beacon is ready
 * (ready event + poll backup, with a timeout so it never spins forever). Clicks
 * before ready still work — the inline loader queues `Beacon('open')`.
 */

export const TRIGGER_ATTRIBUTE = 'data-helpscout-open';
export const LOADING_CLASS = 'is-loading';
export const READY_TIMEOUT_MS = 15000;
export const POLL_INTERVAL_MS = 250;
export const DEFAULT_ERROR =
  'We couldn’t open live chat. Please call us at (253) 872-0300.';

let ready = false;
let watching = false;
let pollId = null;
let timeoutId = null;

function triggers() {
  return Array.from(document.querySelectorAll(`[${TRIGGER_ATTRIBUTE}]`));
}

export function isBeaconAvailable() {
  return typeof window !== 'undefined' && typeof window.Beacon === 'function';
}

// The inline loader's stub carries a `readyQueue`; the real SDK does not.
export function isBeaconReady() {
  return isBeaconAvailable() && !window.Beacon.readyQueue;
}

export function openBeacon() {
  if (!isBeaconAvailable()) return false;
  try {
    window.Beacon('open');
    return true;
  } catch {
    return false;
  }
}

function setLoading(trigger, loading) {
  if (!trigger?.classList) return;
  trigger.classList.toggle(LOADING_CLASS, loading);
  if (loading) {
    trigger.setAttribute('aria-busy', 'true');
  } else {
    trigger.removeAttribute('aria-busy');
  }
}

function statusElement(trigger) {
  const scope = trigger?.closest?.('.pf-help-scout') || document;
  return scope.querySelector('[data-helpscout-status]');
}

function showStatus(trigger, message) {
  const status = statusElement(trigger);
  if (!status) return;
  status.textContent = message;
  status.hidden = false;
}

function stopWatching() {
  if (pollId !== null) {
    clearInterval(pollId);
    pollId = null;
  }
  if (timeoutId !== null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
  watching = false;
}

function clearLoading() {
  triggers().forEach((trigger) => setLoading(trigger, false));
}

function markReady() {
  if (ready) return;
  ready = true;
  stopWatching();
  clearLoading();
}

// Clear the buttons' initial loading state once the Beacon is ready (ready event
// + poll backup), or give up after the timeout so it never spins forever.
export function startWatching() {
  if (watching || ready) return;
  if (!triggers().length) return;

  if (isBeaconReady()) {
    markReady();
    return;
  }

  watching = true;

  if (isBeaconAvailable()) {
    try {
      window.Beacon('on', 'ready', markReady);
    } catch {
      // ignore — the poll is the backup
    }
  }

  pollId = setInterval(() => {
    if (isBeaconReady()) markReady();
  }, POLL_INTERVAL_MS);

  timeoutId = setTimeout(() => {
    stopWatching();
    clearLoading();
  }, READY_TIMEOUT_MS);
}

export function handleClick(event) {
  // Let modifier / non-primary clicks fall through (e.g. open link in new tab).
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  // event.target isn't always an Element (text nodes, document, synthetic events).
  if (!(event.target instanceof Element)) return;
  const trigger = event.target.closest(`[${TRIGGER_ATTRIBUTE}]`);
  if (!trigger) return;

  event.preventDefault();

  // openBeacon returns false only when the Beacon is entirely absent.
  if (!openBeacon()) {
    showStatus(trigger, trigger.dataset.helpscoutError || DEFAULT_ERROR);
  }
}

export function init(target = document) {
  target.addEventListener('click', handleClick);
  startWatching();
}

if (typeof document !== 'undefined') {
  init();
}
